import { createFileRoute, Link } from '@tanstack/react-router';
import { Header, Footer } from '@/components/marketing';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  BookOpen,
  MessageCircle,
  Mail,
  FileQuestion,
  Video,
  Users,
  ExternalLink,
  Search
} from 'lucide-react';

export const Route = createFileRoute('/support')({
  component: SupportPage,
});

const supportChannels = [
  {
    icon: BookOpen,
    title: 'Documentation',
    description: 'Comprehensive guides and API references to help you get started and master ForgeNetwork.',
    link: '/docs',
    linkText: 'Browse Docs',
  },
  {
    icon: MessageCircle,
    title: 'Community Forum',
    description: 'Connect with other developers, share tips, and get help from the community.',
    link: '/community',
    linkText: 'Join Community',
  },
  {
    icon: Mail,
    title: 'Email Support',
    description: 'Need personalized help? Our support team is here to assist you.',
    link: 'mailto:support@forgenetwork.pro',
    linkText: 'Contact Support',
    external: true,
  },
  {
    icon: Video,
    title: 'Video Tutorials',
    description: 'Watch step-by-step tutorials on using ForgeNetwork features.',
    link: 'https://youtube.com/@forgenetwork',
    linkText: 'Watch Videos',
    external: true,
  },
];

const faqItems = [
  {
    question: 'How do I connect my repository?',
    answer: 'Navigate to Dashboard → Repositories → Add Repository. You can connect via GitHub, GitLab, or Bitbucket OAuth, or use a personal access token.',
  },
  {
    question: 'What programming languages are supported?',
    answer: 'ForgeNetwork supports all major programming languages including JavaScript/TypeScript, Python, Java, Go, Rust, C/C++, Ruby, PHP, and many more.',
  },
  {
    question: 'How long does repository analysis take?',
    answer: 'Analysis time depends on repository size. Most repositories under 100MB are analyzed within 2-5 minutes. Larger repositories may take up to 15 minutes.',
  },
  {
    question: 'Is my code secure?',
    answer: 'Yes. Your code is encrypted in transit and at rest. We analyze code in isolated environments and never store your source code permanently. See our Security page for more details.',
  },
  {
    question: 'Can I export my PRDs?',
    answer: 'Yes! PRDs can be exported in Markdown, JSON, and PDF formats. Pro and Enterprise plans also support direct integration with Notion, Confluence, and Linear.',
  },
  {
    question: 'How do I upgrade my plan?',
    answer: 'Go to Dashboard → Settings → Billing to view available plans and upgrade. Changes take effect immediately with prorated billing.',
  },
];

function SupportPage() {
  return (
    <div className="flex min-h-screen flex-col">
      <Header />
      <main className="flex-1 pt-24 pb-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
          {/* Hero */}
          <div className="text-center mb-16">
            <Badge variant="secondary" className="mb-4">Help Center</Badge>
            <h1 className="text-4xl font-bold tracking-tight sm:text-5xl mb-4">
              How Can We Help?
            </h1>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto mb-8">
              Find answers, connect with our community, or get in touch with our support team.
            </p>

            {/* Search Box */}
            <div className="max-w-xl mx-auto">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search for help..."
                  className="w-full pl-12 pr-4 py-3 rounded-lg border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
            </div>
          </div>

          {/* Support Channels */}
          <section className="mb-16">
            <h2 className="text-2xl font-semibold mb-8 text-center">Get Support</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {supportChannels.map((channel) => (
                <div key={channel.title} className="bg-card rounded-lg border p-6 flex flex-col">
                  <div className="flex items-start gap-4 mb-4">
                    <div className="p-3 rounded-lg bg-primary/10">
                      <channel.icon className="w-6 h-6 text-primary" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-lg mb-1">{channel.title}</h3>
                      <p className="text-muted-foreground text-sm">{channel.description}</p>
                    </div>
                  </div>
                  {channel.external ? (
                    <a
                      href={channel.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-auto inline-flex items-center gap-1 text-primary hover:underline"
                    >
                      {channel.linkText}
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  ) : (
                    <Link
                      to={channel.link}
                      className="mt-auto inline-flex items-center text-primary hover:underline"
                    >
                      {channel.linkText} →
                    </Link>
                  )}
                </div>
              ))}
            </div>
          </section>

          {/* FAQ */}
          <section className="mb-16">
            <h2 className="text-2xl font-semibold mb-8 flex items-center justify-center gap-2">
              <FileQuestion className="w-6 h-6" />
              Frequently Asked Questions
            </h2>
            <div className="space-y-4 max-w-3xl mx-auto">
              {faqItems.map((item, index) => (
                <div key={index} className="bg-card rounded-lg border p-6">
                  <h3 className="font-semibold mb-2">{item.question}</h3>
                  <p className="text-muted-foreground">{item.answer}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Enterprise Support */}
          <section className="bg-gradient-to-r from-primary/10 to-primary/5 rounded-2xl p-8 md:p-12 text-center">
            <Users className="w-12 h-12 text-primary mx-auto mb-4" />
            <h2 className="text-2xl font-semibold mb-4">Enterprise Support</h2>
            <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
              Need dedicated support, custom SLAs, or professional services? Our enterprise team is ready to help.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg">
                <Link to="/contact" search={{ type: 'enterprise' }}>Contact Enterprise Sales</Link>
              </Button>
              <Button asChild variant="outline" size="lg">
                <Link to="/enterprise">Learn About Enterprise</Link>
              </Button>
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
}
